import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";

const ViolenceRiskList = ({ records = [], loading, onAdd, onEdit, onDelete }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const filteredRecords = records.filter((r) =>
        Object.values(r).some((v) =>
            String(v ?? "")
                .toLowerCase()
                .includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredRecords.length;
    const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Confirm Delete",
            text: "Do you want to delete this V-RISK-10 assessment?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed && onDelete) {
            await onDelete(id);
            Swal.fire("Deleted!", "Assessment removed.", "success");
        }
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("V-RISK-10 Assessments", 14, 10);

        const tableColumn = [
            "#",
            "Patient Name",
            "Date",
            "Timing",
            "Overall Risk",
        ];

        const tableRows = filteredRecords.map((r, i) => [
            i + 1,
            r.patient_name || "-",
            r.date || "-",
            r.assessment_timing || "-",
            r.overall_risk || "-",
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
        });

        doc.save("vrisk10_assessments.pdf");
    };

    const columns = [
        {
            name: "S.No",
            cell: (_, index) =>
                index + 1 + (currentPage - 1) * rowsPerPage,
            width: "70px",
        },
        {
            name: "Patient Name",
            selector: (r) => r.patient_name || "-",
            sortable: true,
        },
        {
            name: "Date",
            selector: (r) => r.date || "-",
            sortable: true,
        },
        {
            name: "Timing",
            selector: (r) => r.assessment_timing || "-",
            sortable: true,
        },
        {
            name: "Overall Risk",
            selector: (r) => r.overall_risk || "-",
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onEdit(row.id)}
                    >
                        <i className="ri-edit-line"></i>
                    </button>
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(row.id)}
                    >
                        <i className="ri-delete-bin-line"></i>
                    </button>
                </div>
            ),
            width: "130px",
        },
    ];

    return (
        <div>
            {/* Header */}
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="d-flex align-items-center gap-2">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button
                                    className="dropdown-item"
                                    onClick={handleExportPDF}
                                >
                                    <i className="ri-file-pdf-line me-1 text-danger"></i>
                                    PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredRecords}
                                    filename="vrisk10_assessments.csv"
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i>
                                    CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search Assessments..."
                        className="form-control form-control-sm"
                        style={{ width: "260px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} records
                </h6>
                <div>
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={onAdd}
                    >
                        <i className="ri-add-line me-1"></i> Add New
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredRecords}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    highlightOnHover
                    striped
                    responsive
                    progressPending={loading}
                    noDataComponent={
                        <div className="text-center py-4">
                            No records found
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default ViolenceRiskList;
